import type { IconGeometry, IconShape } from "./geometry";
import { Icon, type IconProps } from "./icon";

/**
 * Rendu web de la géométrie partagée.
 *
 * L'adaptateur `<svg>` de `geometry.ts`. Son pendant natif est
 * `@one-nexty/icons-native`, qui lit exactement les mêmes tracés et les rend
 * avec react-native-svg.
 *
 * Les attributs de `IconShape` portent les noms de la spécification SVG, que
 * React accepte tels quels sur ses éléments : un seul `spread` suffit, quelle
 * que soit la primitive. Le `switch` ne sert donc qu'à choisir la BALISE.
 */

function shapeElement(shape: IconShape, key: number) {
  const { tag, ...attrs } = shape;

  switch (tag) {
    case "path":
      return <path key={key} {...attrs} />;
    case "circle":
      return <circle key={key} {...attrs} />;
    case "ellipse":
      return <ellipse key={key} {...attrs} />;
    case "rect":
      return <rect key={key} {...attrs} />;
    case "line":
      return <line key={key} {...attrs} />;
    case "polyline":
      return <polyline key={key} {...attrs} />;
    case "polygon":
      return <polygon key={key} {...attrs} />;
  }
}

/**
 * Fabrique le composant d'une icône à partir de sa géométrie.
 *
 * `fill` n'est posé QUE pour les icônes pleines, et jamais avec la valeur
 * `undefined` : `<Icon>` pose `fill="none"` avant d'étaler les props reçues, si
 * bien qu'un `fill={undefined}` explicite l'écraserait — et un SVG sans `fill`
 * se remplit de noir. Les icônes tracées deviendraient des taches.
 */
export function fromGeometry(geometry: IconGeometry, displayName: string) {
  const IconComponent = (props: IconProps) => (
    <Icon {...props} {...(geometry.solid ? { fill: "currentColor" } : null)}>
      {geometry.shapes.map(shapeElement)}
    </Icon>
  );

  IconComponent.displayName = displayName;
  return IconComponent;
}
